// Sugalarm — read-only modal for inspecting a logged treatment.
// Cycle 34.22: rows in the recent-entries strip and the mobile
// /log/recent drill-down truncate the notes column with ellipsis, so a
// long meal description or exercise label gets cut off. Tap a row and
// this modal opens with the full text plus the kind / value /
// timestamp — pure-read; the cycle-34.20 EditTreatmentModal handles
// edits.

import { useEffect, useState } from "react";

import { fmtTime } from "../../lib/glucose";
import type { Treatment } from "../../lib/types";

interface Props {
  treatment: Treatment | null;
  onClose: () => void;
  /** 24-hour clock preference. Falls back to 12-hour. */
  h24?: boolean;
  /** Display units, used for BG check rows that store the value in
   *  `notes` with a unit suffix. */
  units?: "mg/dL" | "mmol/L";
}

const KIND_LABELS: Record<string, string> = {
  carbs: "Carbs",
  insulin: "Insulin",
  bg_check: "BG check",
  exercise: "Exercise",
  note: "Note",
};

function valueLine(t: Treatment): string | null {
  if (t.event_type === "carbs" && t.carbs_g != null) {
    return `${t.carbs_g} g of carbs`;
  }
  if (t.event_type === "insulin" && t.insulin_u != null) {
    const n = Number(t.insulin_u);
    return `${t.insulin_u} ${n === 1 ? "unit" : "units"} of insulin`;
  }
  return null;
}

function formatLongTimestamp(iso: string, h24: boolean): string {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(d);
  return `${date} · ${fmtTime(iso, { h24 })}`;
}

export function ViewEntryModal({
  treatment,
  onClose,
  h24 = false,
}: Props) {
  const [mounted, setMounted] = useState<boolean>(treatment != null);
  const visible = treatment != null;

  useEffect(() => {
    if (visible) {
      setMounted(true);
      return;
    }
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [visible]);

  // Dismiss on Escape so the modal feels like a proper dialog on the
  // kiosk + desktop variants where a physical keyboard exists.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [visible, onClose]);

  if (!mounted || !treatment) return null;

  const kindLabel = KIND_LABELS[treatment.event_type] ?? treatment.event_type;
  const valueText = valueLine(treatment);
  const notes = (treatment.notes ?? "").trim();
  const source =
    typeof treatment.provenance === "object" && treatment.provenance
      ? (treatment.provenance as Record<string, unknown>).source
      : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${kindLabel} entry`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 355,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: visible ? "rgba(0, 0, 0, 0.32)" : "transparent",
        backdropFilter: "blur(3px)",
        transition: "background 220ms ease-out",
        pointerEvents: visible ? "auto" : "none",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(380px, 100%)",
          maxHeight: "calc(100vh - 80px)",
          overflowY: "auto",
          padding: "22px 24px 20px",
          background: "var(--bg-elev)",
          border: "0.5px solid var(--line-strong)",
          borderRadius: 18,
          boxShadow: "0 16px 48px rgba(0, 0, 0, 0.4)",
          transform: visible
            ? "scale(1) translateY(0)"
            : "scale(0.94) translateY(8px)",
          opacity: visible ? 1 : 0,
          transition: "transform 220ms ease-out, opacity 200ms ease-out",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span
            style={{
              fontSize: "var(--type-floor)",
              fontWeight: 700,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "var(--accent)",
            }}
          >
            {kindLabel}
          </span>
          {source === "photo" && (
            <span
              style={{
                fontSize: "var(--type-floor)",
                fontWeight: 600,
                color: "var(--ink-3)",
                background: "var(--bg-sunk)",
                padding: "2px 8px",
                borderRadius: 999,
                border: "0.5px solid var(--line)",
              }}
            >
              from photo
            </span>
          )}
        </div>

        {valueText && (
          <div
            style={{
              fontFamily: "var(--hero-font)",
              fontVariantNumeric: "tabular-nums",
              fontSize: 26,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: "var(--ink)",
              lineHeight: 1.15,
            }}
          >
            {valueText}
          </div>
        )}

        {notes ? (
          <div
            style={{
              fontSize: 15,
              color: "var(--ink-2)",
              lineHeight: 1.45,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {notes}
          </div>
        ) : (
          <div
            style={{
              fontSize: 13,
              color: "var(--ink-3)",
              fontStyle: "italic",
            }}
          >
            No additional notes.
          </div>
        )}

        <div
          style={{
            fontSize: "var(--type-floor)",
            color: "var(--ink-3)",
            paddingTop: 6,
            borderTop: "0.5px solid var(--line)",
          }}
        >
          {formatLongTimestamp(treatment.timestamp, h24)}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 4,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              appearance: "none",
              border: "0.5px solid var(--line-strong)",
              background: "transparent",
              color: "var(--ink-2)",
              borderRadius: 999,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
